// Move legality and application under Japanese rules with simple ko.
// Positions are immutable: applyMove clones the stone array.

import type { Color, Pt } from '../types.ts';
import {
  BLACK,
  EMPTY,
  WHITE,
  chainAt,
  colorToState,
  neighborsOf,
  type Position,
} from './board.ts';

export type IllegalReason = 'out_of_bounds' | 'occupied' | 'ko' | 'suicide';

export class IllegalMoveError extends Error {
  readonly reason: IllegalReason;
  constructor(reason: IllegalReason, detail?: string) {
    super(detail ? `illegal move (${reason}): ${detail}` : `illegal move (${reason})`);
    this.name = 'IllegalMoveError';
    this.reason = reason;
  }
}

export interface MoveResult {
  position: Position;
  captured: Pt[];                             // ascending order
}

interface Evaluated {
  stones: Uint8Array;
  captured: Pt[];
  koPoint: Pt | null;
}

function evaluateMove(pos: Position, color: Color, pt: Pt): Evaluated | IllegalReason {
  const n = pos.size * pos.size;
  if (!Number.isInteger(pt) || pt < 0 || pt >= n) return 'out_of_bounds';
  if (pos.stones[pt] !== EMPTY) return 'occupied';
  if (pos.koPoint === pt) return 'ko';

  const state = colorToState(color);
  const enemy = state === BLACK ? WHITE : BLACK;
  const stones = pos.stones.slice();
  stones[pt] = state;

  // Remove adjacent enemy chains left with zero liberties.
  const captured: Pt[] = [];
  const checked = new Set<Pt>();
  for (const nb of neighborsOf(pos.size, pt)) {
    if (stones[nb] !== enemy || checked.has(nb)) continue;
    const chain = chainAt(stones, pos.size, nb);
    for (const p of chain.points) checked.add(p);
    if (chain.liberties.length === 0) {
      for (const p of chain.points) {
        stones[p] = EMPTY;
        captured.push(p);
      }
    }
  }
  captured.sort((a, b) => a - b);

  // Suicide (single- or multi-stone) is forbidden.
  const own = chainAt(stones, pos.size, pt);
  if (own.liberties.length === 0) return 'suicide';

  // Simple ko: exactly one stone captured AND the placed group is a lone stone
  // with exactly one liberty (necessarily the vacated point).
  let koPoint: Pt | null = null;
  if (captured.length === 1 && own.points.length === 1 && own.liberties.length === 1) {
    koPoint = captured[0]!;
  }
  return { stones, captured, koPoint };
}

/** Returns the reason a stone move is illegal, or null if legal. */
export function checkMove(pos: Position, color: Color, pt: Pt): IllegalReason | null {
  const result = evaluateMove(pos, color, pt);
  return typeof result === 'string' ? result : null;
}

export function isLegal(pos: Position, color: Color, pt: Pt): boolean {
  return checkMove(pos, color, pt) === null;
}

/** Apply a stone move. Throws IllegalMoveError on occupied / suicide / ko / out-of-bounds. */
export function applyMove(pos: Position, color: Color, pt: Pt): MoveResult {
  const result = evaluateMove(pos, color, pt);
  if (typeof result === 'string') {
    throw new IllegalMoveError(result, `${color} at pt ${pt}`);
  }
  return {
    position: { size: pos.size, stones: result.stones, koPoint: result.koPoint },
    captured: result.captured,
  };
}

/** A pass clears any ko point (the ko retake becomes legal after a pass). */
export function applyPass(pos: Position): Position {
  if (pos.koPoint === null) return pos;
  return { size: pos.size, stones: pos.stones, koPoint: null };
}
